import { z } from "zod";

import {
  formatThumbnailRequestPlainText,
  MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS,
  parseMarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffDefaults,
} from "./marketplace-order-handoff-defaults.js";
import { looksLikeHandoffUrl } from "./marketplace-order-handoff-source.js";
import {
  canonicalTargetRatios,
  StoredTargetRatiosSchema,
  type TargetRatio,
} from "./target-ratios.js";

export const HandoffFieldProvenanceSchema = z.enum([
  "org_default",
  "order_override",
]);
export type HandoffFieldProvenance = z.infer<
  typeof HandoffFieldProvenanceSchema
>;

export const HandoffSnapshotFieldSchema = z.object({
  value: z.string(),
  source: HandoffFieldProvenanceSchema,
  folderLink: z.string().optional(),
});
export type HandoffSnapshotField = z.infer<typeof HandoffSnapshotFieldSchema>;

export const HandoffEmailDeliveryStatusSchema = z.enum([
  "queued",
  "sent",
  "delivered",
  "opened",
  "bounced",
  "legacy",
]);
export type HandoffEmailDeliveryStatus = z.infer<
  typeof HandoffEmailDeliveryStatusSchema
>;

export const HandoffSnapshotEmailAttachmentSchema = z.object({
  filename: z.string(),
  contentType: z.string().optional(),
});
export type HandoffSnapshotEmailAttachment = z.infer<
  typeof HandoffSnapshotEmailAttachmentSchema
>;

export const HandoffSnapshotEmailVersionSchema = z.object({
  version: z.number().int().positive(),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()).default([]),
  from: z.string().nullable().optional(),
  replyTo: z.string().nullable().optional(),
  attachments: z.array(HandoffSnapshotEmailAttachmentSchema).default([]),
  sentAt: z.string(),
  sentBy: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  deliveryStatus: HandoffEmailDeliveryStatusSchema,
  templateVersion: z.string().nullable(),
});
export type HandoffSnapshotEmailVersion = z.infer<
  typeof HandoffSnapshotEmailVersionSchema
>;

export const HandoffSnapshotEmailSchema = z.object({
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()).default([]),
  from: z.string().nullable().default(null),
  replyTo: z.string().nullable().default(null),
  subject: z.string().nullable(),
  html: z.string().nullable(),
  text: z.string().nullable(),
  recorded: z.boolean(),
  attachments: z.array(HandoffSnapshotEmailAttachmentSchema).default([]),
  sentAt: z.string().nullable().default(null),
  sentBy: z.string().nullable().default(null),
  providerMessageId: z.string().nullable().default(null),
  templateVersion: z.string().nullable().default(null),
  deliveryStatus: HandoffEmailDeliveryStatusSchema.nullable().default(null),
  isLegacy: z.boolean().default(false),
  versions: z.array(HandoffSnapshotEmailVersionSchema).default([]),
});
export type HandoffSnapshotEmail = z.infer<typeof HandoffSnapshotEmailSchema>;

export const MarketplaceOrderHandoffSnapshotSchema = z.object({
  recorded: z.boolean(),
  sentAt: z.string().nullable(),
  vendorName: z.string().nullable(),
  orderInstructions: HandoffSnapshotFieldSchema,
  productAccuracy: HandoffSnapshotFieldSchema,
  rules: z.object({
    brandGuidelines: HandoffSnapshotFieldSchema,
    techSpecs: HandoffSnapshotFieldSchema,
    policyRestrictions: HandoffSnapshotFieldSchema,
    dosAndDonts: HandoffSnapshotFieldSchema,
  }),
  brandKit: z.object({
    fontsLicenceProof: HandoffSnapshotFieldSchema,
    coloursHex: HandoffSnapshotFieldSchema,
    logos: HandoffSnapshotFieldSchema,
  }),
  /**
   * Absent on orders sent before the thumbnail request. Present (even when the
   * org left the field blank) once the send snapshotted it.
   */
  thumbnailGuidelines: HandoffSnapshotFieldSchema.optional(),
  targetRatios: StoredTargetRatiosSchema,
  email: HandoffSnapshotEmailSchema,
});
export type MarketplaceOrderHandoffSnapshot = z.infer<
  typeof MarketplaceOrderHandoffSnapshotSchema
>;

const EMPTY_FIELD: HandoffSnapshotField = { value: "", source: "org_default" };

export const EMPTY_HANDOFF_SNAPSHOT: MarketplaceOrderHandoffSnapshot = {
  recorded: false,
  sentAt: null,
  vendorName: null,
  orderInstructions: { value: "", source: "order_override" },
  productAccuracy: EMPTY_FIELD,
  rules: {
    brandGuidelines: EMPTY_FIELD,
    techSpecs: EMPTY_FIELD,
    policyRestrictions: EMPTY_FIELD,
    dosAndDonts: EMPTY_FIELD,
  },
  brandKit: {
    fontsLicenceProof: EMPTY_FIELD,
    coloursHex: EMPTY_FIELD,
    logos: EMPTY_FIELD,
  },
  targetRatios: [],
  email: {
    to: [],
    cc: [],
    bcc: [],
    from: null,
    replyTo: null,
    subject: null,
    html: null,
    text: null,
    recorded: false,
    attachments: [],
    sentAt: null,
    sentBy: null,
    providerMessageId: null,
    templateVersion: null,
    deliveryStatus: null,
    isLegacy: false,
    versions: [],
  },
};

const HEX_PATTERN = /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b/g;
const PRIVATE_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "::1",
  "console.cloud.google.com",
  "console.aws.amazon.com",
  "portal.azure.com",
]);

export function provenanceForValue(
  value: string,
  baseline: string,
): HandoffFieldProvenance {
  return value.trim() === baseline.trim() ? "org_default" : "order_override";
}

export function snapshotHandoffField(
  value: string,
  baseline: string,
): HandoffSnapshotField {
  const trimmed = value.trim();
  return {
    value: trimmed,
    source: provenanceForValue(trimmed, baseline),
    ...(looksLikeHandoffUrl(trimmed) ? { folderLink: trimmed } : {}),
  };
}

export function handoffFieldHasContent(field: HandoffSnapshotField): boolean {
  return field.value.trim().length > 0;
}

export function buildHandoffSnapshot(input: {
  defaults: MarketplaceOrderHandoffDefaults;
  baseline: MarketplaceOrderHandoffDefaults;
  overrideNote?: string | null;
  targetRatios?: readonly TargetRatio[] | null;
  to?: readonly string[];
  cc?: readonly string[];
  sentAt?: string | null;
  vendorName?: string | null;
  email?: Partial<HandoffSnapshotEmail>;
}): MarketplaceOrderHandoffSnapshot {
  const defaults = parseMarketplaceOrderHandoffDefaults(input.defaults);
  const baseline = parseMarketplaceOrderHandoffDefaults(input.baseline);
  const snapshot: MarketplaceOrderHandoffSnapshot = {
    recorded: true,
    sentAt: input.sentAt ?? null,
    vendorName: input.vendorName?.trim() || null,
    orderInstructions: snapshotHandoffField(input.overrideNote ?? "", ""),
    productAccuracy: snapshotHandoffField(
      defaults.productAccuracy,
      baseline.productAccuracy,
    ),
    rules: {
      brandGuidelines: snapshotHandoffField(
        defaults.brandGuidelines,
        baseline.brandGuidelines,
      ),
      techSpecs: snapshotHandoffField(defaults.techSpecs, baseline.techSpecs),
      policyRestrictions: snapshotHandoffField(
        defaults.policyRestrictions,
        baseline.policyRestrictions,
      ),
      dosAndDonts: snapshotHandoffField(
        defaults.dosAndDonts,
        baseline.dosAndDonts,
      ),
    },
    brandKit: {
      fontsLicenceProof: snapshotHandoffField(
        defaults.fontsLicenceProof,
        baseline.fontsLicenceProof,
      ),
      coloursHex: snapshotHandoffField(
        defaults.coloursHex,
        baseline.coloursHex,
      ),
      logos: snapshotHandoffField(defaults.logos, baseline.logos),
    },
    thumbnailGuidelines: snapshotHandoffField(
      defaults.thumbnailGuidelines,
      baseline.thumbnailGuidelines,
    ),
    targetRatios: canonicalTargetRatios(input.targetRatios ?? []),
    email: {
      ...EMPTY_HANDOFF_SNAPSHOT.email,
      to: [...(input.to ?? [])],
      cc: [...(input.cc ?? [])],
      bcc: [...(input.email?.bcc ?? [])],
      from: input.email?.from ?? null,
      replyTo: input.email?.replyTo ?? null,
      subject: input.email?.subject ?? null,
      html: input.email?.html ?? null,
      text: input.email?.text ?? null,
      recorded: Boolean(
        input.email?.html || input.email?.text || input.email?.recorded,
      ),
      attachments: [...(input.email?.attachments ?? [])],
      sentAt: input.email?.sentAt ?? input.sentAt ?? null,
      sentBy: input.email?.sentBy ?? null,
      providerMessageId: input.email?.providerMessageId ?? null,
      templateVersion: input.email?.templateVersion ?? null,
      deliveryStatus: input.email?.deliveryStatus ?? null,
      isLegacy: input.email?.isLegacy ?? false,
      versions: [...(input.email?.versions ?? [])],
    },
  };
  if (!handoffSnapshotHasVisibleFields(snapshot) && !snapshot.email.recorded) {
    return { ...snapshot, recorded: false };
  }
  return snapshot;
}

export function reconstructHandoffSnapshot(input: {
  defaults?: unknown;
  overrideNote?: string | null;
  to?: readonly string[];
  cc?: readonly string[];
  sentAt?: string | Date | null;
  vendorName?: string | null;
}): MarketplaceOrderHandoffSnapshot {
  const defaults = parseMarketplaceOrderHandoffDefaults(input.defaults);
  const sentAt =
    input.sentAt instanceof Date
      ? input.sentAt.toISOString()
      : (input.sentAt ?? null);
  const snapshot = buildHandoffSnapshot({
    defaults,
    baseline: defaults,
    overrideNote: input.overrideNote,
    to: input.to,
    cc: input.cc,
    sentAt,
    vendorName: input.vendorName,
  });
  const legacy: MarketplaceOrderHandoffSnapshot = {
    ...snapshot,
    thumbnailGuidelines: undefined,
    email: {
      ...snapshot.email,
      isLegacy: true,
      deliveryStatus: snapshot.email.recorded
        ? snapshot.email.deliveryStatus
        : "legacy",
    },
  };
  if (!handoffSnapshotHasVisibleFields(legacy) && !legacy.email.recorded) {
    return { ...legacy, recorded: false };
  }
  return legacy;
}

export function parseHandoffSnapshot(
  raw: unknown,
): MarketplaceOrderHandoffSnapshot | null {
  const parsed = MarketplaceOrderHandoffSnapshotSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function presentOrderHandoff(input: {
  handoffSnapshot?: unknown;
  handoffDefaultsSnapshot?: unknown;
  handoffOverrideNote?: string | null;
  recipientEmails?: readonly string[] | null;
  recipientEmail?: string | null;
  ccEmails?: readonly string[] | null;
  sentAt?: string | Date | null;
  vendorName?: string | null;
}): MarketplaceOrderHandoffSnapshot {
  const stored = parseHandoffSnapshot(input.handoffSnapshot);
  if (stored) return stored;
  const to =
    input.recipientEmails && input.recipientEmails.length > 0
      ? input.recipientEmails
      : input.recipientEmail
        ? [input.recipientEmail]
        : [];
  return reconstructHandoffSnapshot({
    defaults: input.handoffDefaultsSnapshot,
    overrideNote: input.handoffOverrideNote,
    to,
    cc: input.ccEmails ?? [],
    sentAt: input.sentAt,
    vendorName: input.vendorName,
  });
}

export function withRecordedHandoffEmail(
  snapshot: MarketplaceOrderHandoffSnapshot,
  email: {
    subject: string;
    html: string;
    text: string;
    from?: string | null;
    replyTo?: string | null;
    to?: readonly string[];
    cc?: readonly string[];
    bcc?: readonly string[];
    attachments?: readonly HandoffSnapshotEmailAttachment[];
    sentAt?: string | null;
    sentBy?: string | null;
    providerMessageId?: string | null;
    templateVersion?: string | null;
    deliveryStatus?: HandoffEmailDeliveryStatus | null;
  },
): MarketplaceOrderHandoffSnapshot {
  const sentAt =
    email.sentAt ??
    snapshot.email.sentAt ??
    snapshot.sentAt ??
    new Date().toISOString();
  const to = email.to ? [...email.to] : snapshot.email.to;
  const cc = email.cc ? [...email.cc] : snapshot.email.cc;
  const bcc = email.bcc ? [...email.bcc] : snapshot.email.bcc;
  const nextVersion: HandoffSnapshotEmailVersion = {
    version: snapshot.email.versions.length + 1,
    subject: email.subject,
    html: email.html,
    text: email.text,
    to,
    cc,
    bcc,
    from: email.from ?? snapshot.email.from,
    replyTo: email.replyTo ?? snapshot.email.replyTo,
    attachments: email.attachments
      ? [...email.attachments]
      : snapshot.email.attachments,
    sentAt,
    sentBy: email.sentBy ?? snapshot.email.sentBy,
    providerMessageId: email.providerMessageId ?? null,
    deliveryStatus: email.deliveryStatus ?? "sent",
    templateVersion: email.templateVersion ?? snapshot.email.templateVersion,
  };
  return {
    ...snapshot,
    recorded: true,
    email: {
      ...snapshot.email,
      to,
      cc,
      bcc,
      from: email.from ?? snapshot.email.from,
      replyTo: email.replyTo ?? snapshot.email.replyTo,
      subject: email.subject,
      html: email.html,
      text: email.text,
      recorded: true,
      attachments: email.attachments
        ? [...email.attachments]
        : snapshot.email.attachments,
      sentAt,
      sentBy: email.sentBy ?? snapshot.email.sentBy,
      providerMessageId:
        email.providerMessageId ?? snapshot.email.providerMessageId,
      templateVersion: email.templateVersion ?? snapshot.email.templateVersion,
      deliveryStatus: email.deliveryStatus ?? "sent",
      isLegacy: false,
      versions: [...snapshot.email.versions, nextVersion],
    },
  };
}

export type HandoffSnapshotFieldKey =
  | "orderInstructions"
  | "productAccuracy"
  | "thumbnailGuidelines"
  | keyof MarketplaceOrderHandoffSnapshot["rules"]
  | keyof MarketplaceOrderHandoffSnapshot["brandKit"];

export interface HandoffSnapshotFieldView {
  key: HandoffSnapshotFieldKey;
  section:
    | "instructions"
    | "productAccuracy"
    | "rules"
    | "brandKit"
    | "thumbnails";
  field: HandoffSnapshotField;
}

export function listHandoffSnapshotFields(
  snapshot: MarketplaceOrderHandoffSnapshot,
  options?: { overridesFirst?: boolean },
): HandoffSnapshotFieldView[] {
  const allRows: HandoffSnapshotFieldView[] = [
    {
      key: "orderInstructions",
      section: "instructions",
      field: snapshot.orderInstructions,
    },
    {
      key: "productAccuracy",
      section: "productAccuracy",
      field: snapshot.productAccuracy,
    },
    {
      key: "brandGuidelines",
      section: "rules",
      field: snapshot.rules.brandGuidelines,
    },
    { key: "techSpecs", section: "rules", field: snapshot.rules.techSpecs },
    {
      key: "policyRestrictions",
      section: "rules",
      field: snapshot.rules.policyRestrictions,
    },
    { key: "dosAndDonts", section: "rules", field: snapshot.rules.dosAndDonts },
    {
      key: "fontsLicenceProof",
      section: "brandKit",
      field: snapshot.brandKit.fontsLicenceProof,
    },
    {
      key: "coloursHex",
      section: "brandKit",
      field: snapshot.brandKit.coloursHex,
    },
    { key: "logos", section: "brandKit", field: snapshot.brandKit.logos },
  ];
  const rows = allRows.filter((row) => handoffFieldHasContent(row.field));

  if (snapshot.thumbnailGuidelines != null && snapshot.sentAt != null) {
    rows.push({
      key: "thumbnailGuidelines",
      section: "thumbnails",
      field: snapshot.thumbnailGuidelines,
    });
  }

  if (!options?.overridesFirst) return rows;
  return [...rows].sort((a, b) => {
    const aOverride = a.field.source === "order_override" ? 0 : 1;
    const bOverride = b.field.source === "order_override" ? 0 : 1;
    return aOverride - bOverride;
  });
}

export function handoffSnapshotHasVisibleFields(
  snapshot: MarketplaceOrderHandoffSnapshot,
): boolean {
  return listHandoffSnapshotFields(snapshot).length > 0;
}

const HANDOFF_COMPARE_FIELD_KEYS: readonly HandoffSnapshotFieldKey[] = [
  "orderInstructions",
  "productAccuracy",
  "brandGuidelines",
  "techSpecs",
  "policyRestrictions",
  "dosAndDonts",
  "fontsLicenceProof",
  "coloursHex",
  "logos",
];

/** Compare Defaults keys. Thumbnail guidelines are omitted when the sent snapshot never recorded them. */
export function listHandoffCompareFieldKeys(
  snapshot: MarketplaceOrderHandoffSnapshot,
): HandoffSnapshotFieldKey[] {
  if (snapshot.thumbnailGuidelines == null) return [...HANDOFF_COMPARE_FIELD_KEYS];
  return [...HANDOFF_COMPARE_FIELD_KEYS, "thumbnailGuidelines"];
}

export type HandoffBriefCopyGroup = "all" | "perOrder" | "rules" | "brandKit";

const COPY_GROUP_SECTIONS: Record<
  Exclude<HandoffBriefCopyGroup, "all">,
  ReadonlySet<HandoffSnapshotFieldView["section"]>
> = {
  perOrder: new Set(["instructions", "productAccuracy"]),
  rules: new Set(["rules"]),
  brandKit: new Set(["brandKit"]),
};

function briefFieldLabel(key: HandoffSnapshotFieldKey): string {
  if (key === "orderInstructions") return "Order-specific instructions";
  return (
    MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.find((item) => item.key === key)
      ?.label ?? key
  );
}

export function formatHandoffBriefGroupPlainText(
  snapshot: MarketplaceOrderHandoffSnapshot,
  group: HandoffBriefCopyGroup,
): string {
  const allowed = group === "all" ? null : COPY_GROUP_SECTIONS[group];
  const lines: string[] = [];
  for (const row of listHandoffSnapshotFields(snapshot)) {
    if (row.key === "thumbnailGuidelines") continue;
    if (allowed && !allowed.has(row.section)) continue;
    lines.push(`${briefFieldLabel(row.key)}: ${row.field.value}`);
  }
  const body = lines.join("\n\n");
  if ((group !== "all" && group !== "rules") || !snapshot.thumbnailGuidelines) return body;
  const thumbnails = formatThumbnailRequestPlainText({
    guidelines: snapshot.thumbnailGuidelines.value,
    ratios: snapshot.targetRatios,
  });
  return body ? `${body}\n\n${thumbnails}` : thumbnails;
}

export function formatHandoffBriefPlainText(
  snapshot: MarketplaceOrderHandoffSnapshot,
): string {
  return formatHandoffBriefGroupPlainText(snapshot, "all");
}

export function extractHexColors(value: string): string[] {
  return Array.from(new Set(value.match(HEX_PATTERN) ?? []));
}

export function extractFontNames(value: string): string[] {
  if (looksLikeHandoffUrl(value)) return [];
  return value
    .split(/[\n,;/]+/)
    .map((part) => part.replace(/\s+\d{2,4}\b.*$/, "").trim())
    .filter(
      (part) =>
        part.length >= 2 && !looksLikeHandoffUrl(part) && !part.startsWith("#"),
    );
}

export function parseHandoffLinkHost(value: string): string | null {
  if (!looksLikeHandoffUrl(value)) return null;
  try {
    return new URL(value.trim()).hostname;
  } catch {
    return null;
  }
}

function isPrivateIpv4(hostname: string): boolean {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(hostname);
  if (!match) return false;
  const octets = match.slice(1).map(Number);
  if (octets.some((octet) => octet > 255)) return false;
  const [a, b] = octets;
  return (
    a === 10 ||
    a === 127 ||
    (a === 192 && b === 168) ||
    (a === 172 && b >= 16 && b <= 31)
  );
}

export function isVendorInaccessibleUrl(value: string): boolean {
  if (!looksLikeHandoffUrl(value)) return false;
  try {
    const url = new URL(value.trim());
    const host = url.hostname.toLowerCase();
    if (PRIVATE_HOSTS.has(host)) return true;
    if (host.endsWith(".internal") || host.endsWith(".localhost")) return true;
    return isPrivateIpv4(host);
  } catch {
    return false;
  }
}

export function countHandoffSectionEdits(
  current: MarketplaceOrderHandoffDefaults,
  baseline: MarketplaceOrderHandoffDefaults,
): { rules: number; brandKit: number; productAccuracy: boolean } {
  const parsed = parseMarketplaceOrderHandoffDefaults(current);
  const base = parseMarketplaceOrderHandoffDefaults(baseline);
  const rules = MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.filter(
    (item) =>
      item.section === "rules" && parsed[item.key].trim() !== base[item.key].trim(),
  ).length;
  const brandKit = MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS.filter(
    (item) =>
      item.section === "brandKit" &&
      parsed[item.key].trim() !== base[item.key].trim(),
  ).length;
  return {
    rules,
    brandKit,
    productAccuracy:
      parsed.productAccuracy.trim() !== base.productAccuracy.trim(),
  };
}
